import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../services/supabaseClient.js';
import { track } from '../../utils/track.js';

// Lesson-boundary punctuation at module/track boundaries, plus three Stripe-trial
// surfaces layered on top: a soft nudge after lesson 2, a strong modal after
// lesson 3, and a block screen if the subscription is canceled / past_due.
//
// props:
//   nextLesson:      { id, title } | null   (null = track complete)
//   onContinue:      () => void             (advance to nextLesson)
//   onExit:          () => void             (track-complete CTA; falls back to onContinue)
//   recovery:        boolean                (remediation re-check pass → "Checkpoint Cleared" / "Continue to Next Concept")
//   completedLesson: { title, sequence_order } | null   (drives lesson-2/3 nudges)
//   learner:         { subscription_status, trial_end, id, ... } | null
//   dark:            boolean                (palette for dark surfaces — used on demo / landing)
//
// SAFETY DEVIATION from the patch: "block on subscription_status === 'none'" is
// NOT enforced — 'none' is the default for every learner that hasn't yet been
// initialized in Stripe, which is everyone until Stripe env vars are live.
// Blocking 'none' would lock out every learner on deploy. We block on the
// explicit terminal states only (canceled / past_due). Once Stripe is wired
// and create-subscription writes 'trialing' on onboarding completion, the
// remaining 'none' rows can be back-filled and the rule tightened.

function hoursUntil(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((t - Date.now()) / 3600000));
}

export default function LessonCompleteOverlay({
  nextLesson, onContinue, onExit, recovery = false,
  completedLesson = null, learner = null, dark = false,
  trialExhausted = false,
}) {
  const [dismissedLesson3, setDismissedLesson3] = useState(false);
  const [dismissedBanner,  setDismissedBanner]  = useState(false);
  const [checkoutLoading,  setCheckoutLoading]  = useState(false);
  const [checkoutError,    setCheckoutError]    = useState(null);

  const C = dark ? {
    card: '#21212b', border: 'rgba(255,255,255,0.10)', text: '#e8e6e1',
    muted: '#6b6760', teal: '#2dd4bf', tealText: '#0f0f12', good: '#34d399',
    upNextBg: 'rgba(45,212,191,0.10)', warnBg: 'rgba(251,191,36,0.10)', warn: '#fbbf24',
    blockBg: 'rgba(248,113,113,0.10)', block: '#f87171',
  } : {
    card: 'var(--bg-elevated)', border: 'var(--border-medium)', text: 'var(--text-primary)',
    muted: 'var(--text-muted)', teal: 'var(--accent-primary)', tealText: 'var(--accent-text)', good: 'var(--progress-strong)',
    upNextBg: 'var(--accent-soft)', warnBg: 'rgba(251,191,36,0.12)', warn: '#d97706',
    blockBg: 'rgba(248,113,113,0.10)', block: '#dc2626',
  };

  const status      = learner?.subscription_status || 'none';
  const inTrial     = status === 'trialing';
  const trialBlock  = status === 'canceled' || status === 'past_due';
  const hoursLeft   = inTrial ? hoursUntil(learner?.trial_end) : null;
  // Lesson-2 / lesson-3 detection: sequence_order is per-module, so this
  // primarily catches the launch A+ track (first module). Acceptable for V1;
  // future tracks can refine via an "overall lesson position" signal.
  const seq         = completedLesson?.sequence_order || 0;
  const showBanner  = inTrial && seq === 2 && !dismissedBanner;
  const showStrong  = inTrial && seq === 3 && !dismissedLesson3;

  // paywall_hit — fire once per mount when a paywall surface actually appears
  // (hard access block or the strong lesson-3 modal). The soft lesson-2 banner
  // is a nudge, not a paywall, so it doesn't qualify.
  const paywallFiredRef = useRef(false);
  useEffect(() => {
    if (!paywallFiredRef.current && (trialBlock || showStrong || trialExhausted)) {
      paywallFiredRef.current = true;
      track('paywall_hit');
    }
  }, [trialBlock, showStrong, trialExhausted]);

  // Add Payment Method → Stripe Checkout setup-mode session. Opens in a new tab.
  // applyCoupon defaults true (mid-trial surfaces — lesson-2 banner, strong
  // lesson-3 modal — get the EARLYBIRD first-month discount). Hard block calls
  // with applyCoupon=false because the trial is already over by then.
  const openCheckout = async ({ applyCoupon = true } = {}) => {
    if (!learner?.id) return;
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const r = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ applyEarlyBirdCoupon: applyCoupon }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d?.url) window.open(d.url, '_blank', 'noopener,noreferrer');
      else setCheckoutError('Could not open payment setup. Please try again in a moment.');
    } catch {
      setCheckoutError('Could not open payment setup. Please try again in a moment.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Conversion → Stripe Checkout in SUBSCRIPTION mode (charges immediately on
  // completion). The first-month promo ($5 off via EARLYBIRD → $16.99 first
  // month, then $21.99) is applied server-side in /api/account. Opens in a new
  // tab; the learner returns to the dashboard with an active subscription. Used
  // by the trial-exhausted paywall and the canceled/past_due reactivation.
  const openConversionCheckout = async () => {
    if (!learner?.id) return;
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const r = await fetch('/api/account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action: 'reactivate' }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d?.url) window.open(d.url, '_blank', 'noopener,noreferrer');
      else setCheckoutError('Could not open checkout. Please try again in a moment.');
    } catch {
      setCheckoutError('Could not open checkout. Please try again in a moment.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const card     = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '22px 20px', textAlign: 'center' };
  const primary  = { width: '100%', padding: '13px 24px', minHeight: 44, borderRadius: 12, border: 'none', background: C.teal, color: C.tealText, fontSize: 14, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', letterSpacing: 0.2, opacity: checkoutLoading ? 0.55 : 1 };
  const muteBtn  = { width: '100%', padding: '10px 16px', marginTop: 8, borderRadius: 10, background: 'transparent', border: `1px solid ${C.border}`, color: C.muted, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' };

  // ── Trial-exhausted hard paywall ──
  // Server-side (402 trial_exhausted) signal: the learner used their free
  // lessons and isn't on a paying plan, so the chat API will refuse every turn.
  // Conversion runs through /api/account (subscription-mode Checkout), which
  // applies the EARLYBIRD first-month discount → $16.99 first month, then
  // $21.99/mo. A secondary "Back to Dashboard" lets them leave without getting stuck.
  if (trialExhausted) {
    return (
      <div style={{ ...card, background: C.warnBg, border: `1px solid ${C.warn}55` }}>
        <p style={{ margin: '0 0 6px', color: C.warn, fontSize: 16, fontWeight: 800, letterSpacing: 0.2 }}>
          Your free lessons are complete.
        </p>
        <p style={{ margin: '0 0 18px', color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
          Continue your preparation — <strong style={{ color: C.text }}>$5 off your first month</strong>, then $21.99/month. Cancel any time.
        </p>
        <button onClick={openConversionCheckout} disabled={checkoutLoading} style={primary}>
          {checkoutLoading ? 'Opening…' : 'Continue — $16.99 first month'}
        </button>
        {onExit && (
          <button onClick={onExit} style={muteBtn}>
            Back to Dashboard
          </button>
        )}
        {checkoutError && <p style={{ marginTop: 10, fontSize: 12, color: C.block }}>{checkoutError}</p>}
      </div>
    );
  }

  // ── Access block (canceled / past_due) — short of a full subscription gate ──
  // Past trial-end. Reactivation (/api/account) applies the EARLYBIRD first-month discount.
  if (trialBlock) {
    return (
      <div style={{ ...card, background: C.blockBg, border: `1px solid ${C.block}55` }}>
        <p style={{ margin: '0 0 4px', color: C.block, fontSize: 16, fontWeight: 800, letterSpacing: 0.2 }}>
          Your access has paused.
        </p>
        <p style={{ margin: '0 0 18px', color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
          Reactivate — <strong style={{ color: C.text }}>$5 off your first month</strong>, then $21.99/month. Cancel any time.
        </p>
        <button onClick={openConversionCheckout} disabled={checkoutLoading} style={primary}>
          {checkoutLoading ? 'Opening…' : 'Reactivate — $16.99 first month'}
        </button>
        {checkoutError && <p style={{ marginTop: 10, fontSize: 12, color: C.block }}>{checkoutError}</p>}
      </div>
    );
  }

  // ── Strong lesson-3 modal — shown INSTEAD of the standard overlay until dismissed ──
  // Last chance to lock in EARLYBIRD before the trial-end paywall.
  if (showStrong) {
    return (
      <div style={{ ...card, background: C.warnBg, border: `1px solid ${C.warn}55` }}>
        <p style={{ margin: '0 0 6px', color: C.warn, fontSize: 16, fontWeight: 800, letterSpacing: 0.2 }}>
          Your 3 free lessons are complete.
        </p>
        <p style={{ margin: '0 0 18px', color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
          Add a card now to get <strong style={{ color: C.text }}>$5 off your first month</strong>,
          then $21.99/month.
        </p>
        <button onClick={() => openCheckout()} disabled={checkoutLoading} style={primary}>
          {checkoutLoading ? 'Opening…' : 'Continue — $16.99 first month'}
        </button>
        <button onClick={() => setDismissedLesson3(true)} style={muteBtn}>
          Remind me later
        </button>
        {checkoutError && <p style={{ marginTop: 10, fontSize: 12, color: C.block }}>{checkoutError}</p>}
      </div>
    );
  }

  // ── Standard overlay (Module Complete / Track Complete / Checkpoint Cleared) ──
  const trackComplete = !nextLesson;
  const onPrimary = trackComplete ? (onExit || onContinue) : onContinue;

  return (
    <div style={card}>
      <div style={{
        width: 44, height: 44, borderRadius: '50%', margin: '0 auto 12px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${C.good}22`, color: C.good, fontSize: 22, fontWeight: 800,
      }}>
        ✓
      </div>

      <p style={{ margin: '0 0 4px', color: C.good, fontSize: 16, fontWeight: 800, letterSpacing: 0.2 }}>
        {trackComplete ? 'Track Complete' : recovery ? 'Checkpoint Cleared' : 'Module Complete'}
      </p>

      {trackComplete ? (
        <p style={{ margin: '0 0 18px', color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
          You've completed every lesson in this track. Outstanding work.
        </p>
      ) : (
        <div style={{ margin: '0 0 18px' }}>
          <p style={{ margin: '0 0 8px', color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
            {recovery ? 'Gap repaired — you passed the re-check.' : 'Nice work — that wraps this module.'}
          </p>
          <div style={{ display: 'inline-block', padding: '7px 14px', borderRadius: 10, background: C.upNextBg }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginRight: 7 }}>Up next</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{nextLesson.title}</span>
          </div>
        </div>
      )}

      <button onClick={onPrimary} style={primary}>
        {trackComplete ? 'Back to Tracks' : recovery ? 'Continue to Next Concept →' : 'Continue →'}
      </button>

      {/* Soft lesson-2 trial nudge — below the CTA, dismissible. Standard
          Continue still works without engaging the nudge. */}
      {showBanner && (
        <div style={{ marginTop: 14, padding: '11px 14px', borderRadius: 10, background: C.warnBg, border: `1px solid ${C.warn}33`, textAlign: 'left' }}>
          <p style={{ margin: '0 0 6px', fontSize: 12, color: C.text, fontWeight: 600 }}>
            Add a card before your free lessons end to get $5 off your first month.
          </p>
          <p style={{ margin: '0 0 6px', fontSize: 11, color: C.muted, lineHeight: 1.4 }}>
            Then $21.99/month. Cancel any time.
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              onClick={() => openCheckout()}
              disabled={checkoutLoading}
              style={{ background: 'none', border: 'none', color: C.warn, fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
            >
              {checkoutLoading ? 'Opening…' : 'Get $5 Off First Month →'}
            </button>
            <button
              onClick={() => setDismissedBanner(true)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.muted, fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
            >
              Dismiss
            </button>
          </div>
          {checkoutError && <p style={{ marginTop: 6, fontSize: 11, color: C.block }}>{checkoutError}</p>}
        </div>
      )}
    </div>
  );
}
